import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useSelector } from "src/store";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Popover, Typography } from "@mui/material";
import { DangerOutlinedButton, SecondaryButton } from "@shared/CustomButton";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTableCell } from "@shared/table/MinimalTableComponents";
import { Tooltip } from "@shared/Tooltip";

import { DeleteProps } from "./RepositoriesPage";
import { useStyles } from "./RepositoriesPage.style";

const fdroidRepoBaseUrl = "https://apps.emteria.com/fdroid/";

interface Props {
    repo: API.FdroidRepo;
    index: number;
    provisioningEnabled: boolean;
    handleOpenDeleteDialog: (repo: DeleteProps) => void;
}

const RepositoryTableItem = (props: Props) => {
    const { t } = useTranslation(["pages"]);
    const { classes } = useStyles();
    const navigate = useNavigate();
    const provisionings = useSelector(selectProvisionings);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const relatedProvisionings = provisionings.reduce((accumulator: { id: number; name: string }[], provisioning) => {
        const hasRepo = provisioning.settings?.fDroidRepos.some((fdroidRepo) => fdroidRepo.id === props.repo.id);
        if (hasRepo) {
            accumulator.push({
                id: provisioning.id,
                name: provisioning.name,
            });
        }
        return accumulator;
    }, []);

    const handleProvisioningClick = (provisioningId: number) => {
        void navigate(`/dashboard/provisioning/${provisioningId}`);
    };

    const handlePopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    return (
        <HoverRow clickable={false}>
            <MinimalTableCell style={{ maxWidth: 300 }}>
                <a href={`${fdroidRepoBaseUrl}${props.repo.name}`} target="_blank" rel="noreferrer">
                    {`${fdroidRepoBaseUrl}${props.repo.name}`}
                </a>
            </MinimalTableCell>
            <MinimalTableCell style={{ maxWidth: 220 }}>
                <Tooltip title={props.repo.title}>
                    <Typography>{props.repo.title}</Typography>
                </Tooltip>
            </MinimalTableCell>
            <MinimalTableCell style={{ maxWidth: 220 }}>
                <Tooltip title={props.repo.description}>
                    <Typography>{props.repo.description}</Typography>
                </Tooltip>
            </MinimalTableCell>
            {props.provisioningEnabled ? (
                <MinimalTableCell style={{ maxWidth: 100 }} align="center">
                    {relatedProvisionings.length ? (
                        <>
                            <SecondaryButton onClick={handlePopoverOpen}>
                                <i className="fa-solid fa-check" />
                            </SecondaryButton>
                            <Popover
                                id={id}
                                open={open}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                className={classes.popoverContentCotainer}
                                anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "center",
                                }}
                                transformOrigin={{
                                    vertical: "top",
                                    horizontal: "center",
                                }}
                            >
                                <div className={classes.popoverContentCotainer}>
                                    {relatedProvisionings.map((item) => {
                                        return (
                                            <SecondaryButton key={item.id} onClick={() => handleProvisioningClick(item.id)}>
                                                <Typography>{item.name}</Typography>
                                            </SecondaryButton>
                                        );
                                    })}
                                </div>
                            </Popover>
                        </>
                    ) : (
                        <i className="fa-sharp fa-solid fa-xmark" />
                    )}
                </MinimalTableCell>
            ) : null}
            <MinimalTableCell style={{ maxWidth: 80 }} align="right">
                <DangerOutlinedButton
                    tooltipProps={{ title: t("pages:repositoryPage.tooltip.delete") }}
                    onClick={() =>
                        props.handleOpenDeleteDialog({
                            title: props.repo.title,
                            subscriptionId: props.repo.subscriptionId,
                            id: props.repo.id,
                        })
                    }
                >
                    <i className="fas fa-trash-alt" />
                </DangerOutlinedButton>
            </MinimalTableCell>
        </HoverRow>
    );
};

export default RepositoryTableItem;
